import { Injectable } from '@angular/core';
import { UserAuthenticationService } from '../../../../../../libs/administration/src/lib/services/user-authentication.service';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { NEVER, Observable, catchError, finalize, map, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SpinnerService, V2_MANAGED } from '@zayan-erp/shared';
import { ErpSsoService } from '../../authentication-module/erp-sso.service';

@Injectable()
export class InterceptorService implements HttpInterceptor {
  constructor(
    private _authService: UserAuthenticationService,
    private _spinnerService: SpinnerService,
    private _sso: ErpSsoService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const ignoreUrls: string[] = [environment.s3Url, environment.s3PublicUrl];
    // Refresh and revoke carry their tokens in the body, and refreshing can't fix a 401 from them.
    const isTokenCall = request.url.startsWith(`${environment.baseUrl}token/`);
    const isApiCallingV2 = request.url.includes('/api/v2');
    const shouldAddToken =
      !isTokenCall && this._authService.isLogin() && ignoreUrls.filter(x => request.url.indexOf(x) >= 1).length === 0;
    const sentToken = shouldAddToken ? this._authService.getToken() : null;

    return next.handle(sentToken ? this.#withToken(request, sentToken) : request).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          // Check if response body has success property set to false
          if (event.body && typeof event.body === 'object' && event.body.success === false && !isApiCallingV2) {
            // Create an HttpErrorResponse from the successful response
            const error = new HttpErrorResponse({
              error: event.body,
              headers: event.headers,
              status: event.body.status || 400,
              statusText: event.body.message || 'Some unknown error has occurred',
              url: event.url || undefined
            });
            throw error;
          }
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) =>
        error.status === 401 && sentToken ? this.#refreshAndRetry(request, next, sentToken) : throwError(() => error)
      ),
      finalize(() => {
        // Skip stop for V2 requests — ApiCallingV2Service manages its own spinner lifecycle.
        // Only call stop here for V1 (ApiCallingService) requests.
        if (!request.context.get(V2_MANAGED)) {
          this._spinnerService.handleSpinnerStop();
        }
      })
    );
  }

  /**
   * Renews the session once for every request that got a 401, then retries each with the new token. When the
   * session can't be renewed, the ERP sign-in starts and the failed requests never settle: the page is about to
   * be left, so they raise no error toasts and the loading overlay stays up until then.
   */
  #refreshAndRetry(request: HttpRequest<any>, next: HttpHandler, sentToken: string): Observable<HttpEvent<any>> {
    if (this._sso.isRestartingSignIn()) return NEVER;

    // Another request already renewed the token this one was sent with.
    const current = this._authService.getToken();
    if (current !== sentToken && this._authService.isLogin()) return next.handle(this.#withToken(request, current));

    return this._authService.refreshToken(sentToken).pipe(
      catchError(() => {
        this._sso.restartSignIn();
        return NEVER;
      }),
      switchMap(() => next.handle(this.#withToken(request, this._authService.getToken())))
    );
  }

  #withToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({ headers: request.headers.set('Authorization', `Bearer ${token}`) });
  }
}
